import * as React from "react";
import { makeStyles, Text, Button } from "@fluentui/react-components";
import { Copy16Regular, Open16Regular } from "@fluentui/react-icons";
import CardAccent from "../shared/CardAccent";
import { copyToClipboard, openReplyForm } from "../../../services/outlookActions";

const useStyles = makeStyles({
  card: {
    backgroundColor: "var(--bg-elevated, #FFFFFF)",
    border: "1px solid var(--border-primary, #E0E2E6)",
    borderRadius: "12px",
    boxShadow: "var(--shadow-sm, 0 1px 2px rgba(11,17,26,0.06))",
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    ":hover": {
      boxShadow: "var(--shadow-md, 0 4px 8px rgba(11,17,26,0.08))",
      transform: "translateY(-1px)",
    },
  },
  content: {
    padding: "16px",
  },
  title: {
    fontSize: "14px",
    fontWeight: "600",
    color: "var(--text-primary, #0B111A)",
    marginBottom: "12px",
    display: "block",
  },
  subject: {
    fontSize: "12px",
    fontWeight: "500",
    color: "var(--text-tertiary, #6E747D)",
    marginBottom: "8px",
    display: "block",
  },
  subjectValue: {
    fontWeight: "600",
    color: "var(--text-secondary, #4A4F57)",
  },
  bodyPreview: {
    fontSize: "13px",
    lineHeight: "1.55",
    color: "var(--text-secondary, #4A4F57)",
    padding: "12px",
    backgroundColor: "var(--bg-secondary, #F8F9FA)",
    borderRadius: "8px",
    border: "1px solid var(--border-primary, #E0E2E6)",
    maxHeight: "300px",
    overflowY: "auto",
    "& p": {
      margin: "0 0 8px 0",
    },
    "& p:last-child": {
      marginBottom: "0",
    },
    "& ul, & ol": {
      paddingLeft: "20px",
      margin: "4px 0",
    },
    "& a": {
      color: "var(--text-link, #13A3E0)",
    },
  },
  actions: {
    display: "flex",
    gap: "8px",
    marginTop: "14px",
    paddingTop: "12px",
    borderTop: "1px solid var(--border-primary, #E0E2E6)",
  },
  copyBtn: {
    minWidth: "auto",
    fontSize: "12px",
  },
  openBtn: {
    minWidth: "auto",
    fontSize: "12px",
    backgroundColor: "#FFB500",
    color: "#0B111A",
    fontWeight: "600",
    border: "none",
    ":hover": {
      backgroundColor: "#E6A300",
    },
    ":active": {
      backgroundColor: "#CC9200",
    },
  },
  copiedText: {
    fontSize: "11px",
    color: "var(--color-success, #7ED956)",
    alignSelf: "center",
    marginLeft: "4px",
  },
});

/**
 * Strip HTML tags to get plain text for clipboard copy.
 */
function htmlToPlainText(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

/**
 * DraftReplyCard — reply preview with Copy and Open in reply form buttons.
 *
 * @param {{ card: { subject?: string, body_html?: string, body_markdown?: string, title?: string, actions?: Array } }} props
 */
const DraftReplyCard = ({ card }) => {
  const styles = useStyles();
  const [copied, setCopied] = React.useState(false);

  const bodyHtml = card.body_html || "";
  const plainText =
    card.body_markdown || htmlToPlainText(bodyHtml);

  const handleCopy = async () => {
    const success = await copyToClipboard(plainText);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpen = () => {
    openReplyForm(bodyHtml || `<p>${plainText}</p>`);
  };

  return (
    <div className={styles.card}>
      <CardAccent />
      <div className={styles.content}>
        <Text className={styles.title}>{card.title || "Draft Reply"}</Text>

        {card.subject && (
          <Text className={styles.subject}>
            Subject: <span className={styles.subjectValue}>{card.subject}</span>
          </Text>
        )}

        {bodyHtml ? (
          <div
            className={styles.bodyPreview}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        ) : (
          <div className={styles.bodyPreview}>
            {plainText.split("\n").map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        )}

        <div className={styles.actions}>
          <Button
            className={styles.copyBtn}
            appearance="subtle"
            size="small"
            icon={<Copy16Regular />}
            onClick={handleCopy}
          >
            {copied ? "Copied!" : "Copy"}
          </Button>
          <Button
            className={styles.openBtn}
            size="small"
            icon={<Open16Regular />}
            onClick={handleOpen}
          >
            Open in new window
          </Button>
          {copied && <Text className={styles.copiedText}>✓</Text>}
        </div>
      </div>
    </div>
  );
};

export default DraftReplyCard;
